export interface DateRangeLabels {
	from: string
	until: string
}

const NL_LABELS: DateRangeLabels = { from: 'Vanaf', until: 'Tot' }

// App language → BCP 47 tag for Intl.DateTimeFormat
const LOCALE_TAGS: Record<string, string> = {
	nl: 'nl-NL',
	vl: 'nl-BE',
	fr: 'fr-FR',
	en: 'en-GB',
	de: 'de-DE',
	ja: 'ja-JP',
}

function localeTag(lang: string) {
	return LOCALE_TAGS[lang] ?? LOCALE_TAGS.nl!
}

/**
 * Format an instant as a calendar date pinned to a fixed timezone (default
 * Amsterdam, where the audience is), so SSR and client render identical text
 * regardless of the runtime's local timezone.
 */
export function formatInstantDate(
	value: Date | string,
	lang = 'nl',
	timeZone = 'Europe/Amsterdam',
): string {
	return new Intl.DateTimeFormat(localeTag(lang), {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		timeZone,
	}).format(new Date(value))
}

/**
 * Human date range for an event, collapsing shared month/year
 * (e.g. "12 - 18 mei 2026"). Client-safe; defaults to Dutch.
 *
 * Event dates are date-only values stored as UTC midnight, so everything is
 * formatted and compared in UTC: local-timezone formatting would make SSR
 * (UTC server) and the browser disagree — hydration mismatches and
 * off-by-one dates for anyone west of UTC.
 */
export function formatEventDateRange(
	beginDate: Date | string | null,
	endDate: Date | string | null,
	lang = 'nl',
	labels: DateRangeLabels = NL_LABELS,
): string {
	const begin = beginDate ? new Date(beginDate) : null
	const end = endDate ? new Date(endDate) : null

	const tag = localeTag(lang)
	const fmt = (d: Date, options: Intl.DateTimeFormatOptions) =>
		new Intl.DateTimeFormat(tag, { ...options, timeZone: 'UTC' }).format(d)
	const full = (d: Date) =>
		fmt(d, { day: 'numeric', month: 'long', year: 'numeric' })

	if (!begin && !end) return ''
	if (!begin) return `${labels.until} ${full(end as Date)}`
	if (!end) return `${labels.from} ${full(begin)}`

	if (
		begin.getUTCMonth() === end.getUTCMonth() &&
		begin.getUTCFullYear() === end.getUTCFullYear()
	) {
		if (begin.getUTCDate() === end.getUTCDate()) return full(begin)
		return `${fmt(begin, { day: 'numeric' })} - ${full(end)}`
	}

	if (begin.getUTCFullYear() === end.getUTCFullYear()) {
		return `${fmt(begin, { day: 'numeric', month: 'long' })} - ${full(end)}`
	}

	return `${full(begin)} - ${full(end)}`
}
